import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {combineAll, filter, map, take, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs/Subject';
import {from} from 'rxjs';

import {ConfirmModalComponent} from '@ispm/layout/components/modals/confirm-modal/confirm-modal.component';
import {ConfirmModalLoaderPayload} from '@ispm/layout/classes/confirm-modal-loader-payload';
import {DynamicModalLoaderService} from '@ispm/layout/services/dynamic-modal-loader.service';
import {IRevision, IRevisionCheckpoint} from '../../interfaces/revision.interface';
import {IWorkflowRequest} from '../../interfaces/workflow-request.interface';
import {ReviseService} from '../../services/revise.service';
import {ReviseType} from '../../constants/revise-types.constant';
import {WorkflowBarActionsCreatorService} from '@ispm/shared/components/workflow-bar/services/workflow-bar-actions-creator.service';
import {WorkflowConfigService} from '../../services/workflow-config.service';
import {WorkflowService} from '../../services/workflow.service';
import {tooltipPositions} from '@ispm/shared/directives/tooltips/tooltip.directive';

import Timer = NodeJS.Timer;
import {ConfigurationStoreHandler} from "../../../configuration/services/configuration-store-handler.service";
import {Subscription} from "rxjs";

@Component({
  selector: 'gen-revise',
  templateUrl: './revise.component.html',
  styleUrls: ['./revise.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReviseComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  revisionId: string;
  @Input()
  testType: string;
  @Input()
  templatesConfiguration: Array<{ usage: 'revision' | 'test', 'reference': string, name?: string }> = [];
  @Input()
  tooltipHtml: string;
  @Input()
  tooltipPosition: tooltipPositions;
  @Input()
  templateDisplayText: string = 'Review';
  @Input()
  disabled: boolean = false;
  @Output()
  reviseCompleted: EventEmitter<IWorkflowRequest> = new EventEmitter<IWorkflowRequest>();

  public checkpoints: Array<IRevisionCheckpoint>;
  public backCheckpoint: IRevisionCheckpoint;

  protected revision: IRevision;
  protected snapshot: any;
  protected destroyer$: Subject<null> = new Subject<null>();
  protected timeoutRef: Timer;

  protected currentConfig$: Subscription;
  private isPathwayTest :Boolean = false;

  constructor(
    protected workflowBarAC: WorkflowBarActionsCreatorService,
    protected reviseService: ReviseService,
    protected workflowConfigService: WorkflowConfigService,
    protected popupLoader: DynamicModalLoaderService,
    protected changeDetector: ChangeDetectorRef,
    protected workflowService: WorkflowService,
    protected _zone: NgZone,
    protected configurationStoreHandler: ConfigurationStoreHandler,
  ) {
    this.currentConfig$ = configurationStoreHandler.getCurrentConfig().pipe(take(1)).subscribe((data) => {
      if (!!(data && data.isPathwayTest)) {
        this.isPathwayTest = data.isPathwayTest;
      }
    });
  }

  protected _workflow: IWorkflowRequest;

  get workflow() {
    return this._workflow;
  };

  @Input()
  set workflow(workflow: IWorkflowRequest) {
    this._workflow = workflow;
    this.updateRevision();
  };

  public _visible: boolean;

  get visible(): boolean {
    return this._visible;
  }

  set visible(_visible: boolean) {
    this._visible = _visible;

  }

  protected _optionsVisibility: boolean = false;

  get optionsVisibility() {
    return this._optionsVisibility;
  }

  set optionsVisibility(status: boolean) {
    this._optionsVisibility = status;
    if (this.optionsVisibility) {
      this.takeWorkflowBarSnapshot();
    }
  }

  ngOnInit() {
    if (!this.workflow) {
      this.workflowConfigService.getActiveWorkflow()
        .pipe(
          takeUntil(this.destroyer$.asObservable()),
          filter(workflow => !!workflow)
        )
        .subscribe((workflow: IWorkflowRequest) => {
          this.workflow = workflow;
          this.updateRevision();
        });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.revisionId && ((changes.revisionId.currentValue !== changes.revisionId.previousValue) || (changes.revisionId.firstChange))
      && (changes.templatesConfiguration && (changes.templatesConfiguration.currentValue !== changes.templatesConfiguration.previousValue) || changes.templatesConfiguration.firstChange)
    ) {
      this.updateRevision();
    }
  }

  async updateRevision() {
    if (!(this.revisionId && this.workflow)) { return; }

    this.revision = this.workflow.revisions.find(revision => revision.id === this.revisionId);
    this.visible = ["none", this.revisionId].indexOf(this.workflow.activeRevision.toLowerCase()) > -1;
    this.templatesConfiguration = this.templatesConfiguration || [];
    const config = await
      from(this.templatesConfiguration)
        .pipe(
          filter(template => template.usage === 'revision'),
          map(conf => {
            return this.workflowService.getWorkflowJobTemplate(conf.reference, false)
              .pipe(
                map((workflowTemplate) => {
                  const returnObj = {
                    display: conf.name || this.templateDisplayText,
                    revertToStepPurpose: conf.reference, // for using the same logic on the ui
                    type: ReviseType.TEMPLATE
                  };
                  if (workflowTemplate.items[0].ui) {
                    returnObj['ui'] = workflowTemplate.items[0].ui;
                    ReviseService.setCheckpointDisplayName(returnObj);
                  }
                  return returnObj;
                })
              );
          }),
          combineAll()
        ).toPromise() as Array<IRevisionCheckpoint>;
    this.checkpoints = ReviseService.filterAvailableCheckpoints(this.workflow.revisions);
    this.checkpoints.reverse();
    const size = this.checkpoints.length;
     this.backCheckpoint = size ? this.checkpoints[size -1] : undefined;
    if (config) {
      config.forEach((checkpoint) => {
        if (checkpoint) {
          this.checkpoints.push(checkpoint);
          const size = this.checkpoints.length;
          this.backCheckpoint = size ? this.checkpoints[size -1] : undefined;
        }
      });
    }
    this.changeDetector.markForCheck();


  }

  public setWorkflowBar(barItems: Array<any> = null) {
    this._zone.runOutsideAngular(() => {
      if (this.timeoutRef) {
        clearTimeout(this.timeoutRef);
      }
      this.timeoutRef = setTimeout(() => this.emitWorkflowBarUpdate(barItems), 0);
    });
  }

  public emitWorkflowBarUpdate(barItems: Array<any> = null) {
    if (!barItems && this.snapshot) {
      this.workflowBarAC.updateWorkflowBar(this.snapshot.workflow_status, this.snapshot.workflow_bar);
    } else {
      this.workflowBarAC.updateWorkflowBar('', barItems);
    }
  }

  public revise(option: IRevisionCheckpoint) {
    let content: string = `Are you sure you want to revise the current test to ${option.display} step?`;
    let approveBtnText: string = 'Yes';
    let declineBtnText: string = 'Cancel';
    if (this.isPathwayTest){
        content = `Please confirm that the diagnosis has not changed and you are revising the treatment or advancing to the next step in treatment`;
        approveBtnText = 'Confirm';
    }
    this.reviseService.openReviseModal({
      component: ConfirmModalComponent,
      payload: new ConfirmModalLoaderPayload('',content , approveBtnText, declineBtnText),
      revisionCheckpoint: option,
      revisionId: this.revisionId,
      workflowId: this.workflow.id
    })
      .subscribe(res => {
        if (res) {
          this.reviseCompleted.emit(res);
        }
      });
  }

  ngOnDestroy(): void {
    if(this.currentConfig$){
      this.currentConfig$.unsubscribe();
    }
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  private takeWorkflowBarSnapshot() {
    this.workflowBarAC.getState()
      .pipe(take(1))
      .subscribe(snapshot => {
        this.snapshot = snapshot;
      });
  }


}


